/* eslint-disable @typescript-eslint/no-empty-function */
import { FC, useEffect, useState, useMemo, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { X } from 'react-feather';
import styles from '../DeactivateProductsDrawer/DeactivateProductsDrawer.module.css';
import { DeactivateModifiersDrawerProps } from './DeactivateModifiersDrawer.types';
import { t } from 'i18next';
import { useDeactivateModifiers } from '@/services/modifiers/modifiers.service';
import {
  EditDrawerSuccessAndFailure,
  EntityToUpdate,
} from '@/pages/Franchisee/SharedComponents/EditDrawerSuccessAndFailure';
import { EditDrawerProcessing } from '../../SharedComponents/EditDrawerProcessing';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceUpdateAPIResponse } from '@/types/stores.types';
import { useSTKContext } from '@/context/context';
import { getStores } from '@/services/menu/menus.helper.service';
import { CalendarAlert } from '@/utils/CalendarAlert/CalendarAlert';

export const DeactivateModifiersDrawer: FC<DeactivateModifiersDrawerProps> = ({
  isOpen,
  onClose,
  storeModifiers,
  modifierCategory,
  afterSave,
  setPriceDeviationsData: _setPriceDeviationsData,
}) => {
  void _setPriceDeviationsData;
  const [state, setState] = useState(isOpen);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivationComplete, setDeactivationComplete] = useState(false);
  const [deactivationResponse, setDeactivationResponse] = useState<PriceUpdateAPIResponse | undefined>();
  const { storesAndGroups } = useSTKContext();

  const { mutate: deactivateModifiers } = useDeactivateModifiers();

  const hasMandatorySelectedModifiers = useMemo(
    () => storeModifiers.some((m) => m?.isMandatory === true),
    [storeModifiers],
  );

  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    if (storesAndGroups.origin === 'tab-store') {
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet).sort();
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      return (storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || []).sort();
    }

    return [];
  }, [storesAndGroups]);

  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  useEffect(() => {
    setState(isOpen);
    if (!isOpen) {
      setIsDeactivating(false);
      setDeactivationComplete(false);
      setDeactivationResponse(undefined);
    }
  }, [isOpen]);

  const requestPayload = useMemo(() => {
    const selectedStores = getStores(storesAndGroups);
    const modifierKeys = storeModifiers
      .filter((m) => m?.isMandatory !== true)
      .map((m) => ({
        variantId: m.variantId ?? '',
        slotId: m.slotId,
        modifierId: m.modifierId,
        modifierWeightId: m.modifierWeightId,
      }));

    return {
      stores: selectedStores,
      modifierKeys,
      modifierCategory,
    };
  }, [storeModifiers, storesAndGroups, modifierCategory]);

  const uniqueStoreCount = useMemo(() => requestPayload.stores.length, [requestPayload]);

  const handleConfirmDeactivation = () => {
    if (hasMandatorySelectedModifiers || requestPayload.modifierKeys.length === 0) {
      return;
    }
    setIsDeactivating(true);
    deactivateModifiers(requestPayload, {
      onSuccess: (response) => {
        setDeactivationResponse(response as PriceUpdateAPIResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        afterSave();
      },
      onError: (error: any) => {
        if (
          error?.response?.status === 504 ||
          error?.code === 'ECONNABORTED' ||
          error?.message?.includes('timeout') ||
          error?.message?.includes('Gateway Timeout')
        ) {
          const timeoutResponse = {
            failedStores: [],
            status: 'TIMEOUT',
            message: t('requestProcessingModal.timeout.message'),
          } as unknown as PriceUpdateAPIResponse;
          setDeactivationResponse(timeoutResponse);
          setDeactivationComplete(true);
          setIsDeactivating(false);
          afterSave();
          return;
        }
        const errorResponse = {
          failedStores: [],
          status: 'ERROR',
          message: error?.message || t('deactivateModifiers.errorMessage'),
        } as unknown as PriceUpdateAPIResponse;
        setDeactivationResponse(errorResponse);
        setDeactivationComplete(true);
        setIsDeactivating(false);
        afterSave();
      },
    });
  };

  const onCloseDrawer = () => {
    setDeactivationComplete(false);
    setIsDeactivating(false);
    setDeactivationResponse(undefined);
    setState(false);
    onClose();
  };

  return (
    <div>
      <SwipeableDrawer open={state} onClose={onCloseDrawer} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('deactivateModifiers.title')}</h1>
            </div>
            <button type="button" onClick={onCloseDrawer}>
              <X data-testid="x-icon" size={24} color="var(--black-900)" />
            </button>
          </header>
          {(isDeactivating || (deactivationComplete && deactivationResponse)) && (
            <div className={styles.alertBanner}>
              <CalendarAlert />
            </div>
          )}

          <div className={styles.content}>
            {isDeactivating ? (
              <EditDrawerProcessing count={uniqueStoreCount} entityToUpdate={EntityToUpdate.MENU} />
            ) : deactivationComplete && deactivationResponse ? (
              <div className={styles.successFailureWrapper}>
                <EditDrawerSuccessAndFailure
                  apiResponse={deactivationResponse as PriceUpdateAPIResponse}
                  entityToUpdate={EntityToUpdate.MENU}
                />
              </div>
            ) : (
              <>
                <div className={styles.confirmationContent}>
                  <h2 className={styles.confirmationTitle}>{t('deactivateModifiers.confirmDeactivation')}</h2>
                  <p className={styles.deactivationInfo}>{t('deactivateModifiers.deactivationInfoText')}</p>
                  {hasMandatorySelectedModifiers && (
                    <p className={styles.deactivationInfo}>
                      {t('deactivateModifiers.mandatoryModifiersSelectedTooltip')}
                    </p>
                  )}

                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th className={styles.productHeader}>{t('editMenuPrice.modifierColumn')}</th>
                          {allStoreGroupNames.map((storeGroupName) => {
                            const { count, total } = getStoreGroupsCount(storeGroupName);
                            return (
                              <th key={storeGroupName} className={styles.priceHeader}>
                                <div className={styles.headerWithCount}>
                                  <div className={styles.headerName}>{storeGroupName}</div>
                                  <div className={styles.headerCount}>
                                    {count} / {total}
                                  </div>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {storeModifiers.map((modifier, index) => (
                          <tr key={`${modifier.modifierId}-${modifier.slotId}-${index}`}>
                            <td className={styles.productCell}>
                              <div className={styles.productName}>{modifier.modifierName}</div>
                              <div className={styles.productDescription}>{modifier.modifierDescription}</div>
                            </td>
                            {allStoreGroupNames.map((storeGroupName) => (
                              <td key={storeGroupName} className={styles.priceCell}>
                                {t('common.inactive')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className={styles.footer}>
                  <div className={styles.alertContainer}>
                    <CalendarAlert />
                  </div>

                  <div className={styles.buttonContainer}>
                    <Button
                      className={styles.cancelButton}
                      onClick={onCloseDrawer}
                      variant={ButtonVariants.Secondary}
                      type="button"
                      disabled={isDeactivating}
                    >
                      {t('editMenuPrice.cancel')}
                    </Button>
                    <Button
                      className={styles.submitButton}
                      onClick={handleConfirmDeactivation}
                      variant={ButtonVariants.Primary}
                      type="button"
                      isLoading={isDeactivating}
                      disabled={
                        isDeactivating || hasMandatorySelectedModifiers || requestPayload.modifierKeys.length === 0
                      }
                    >
                      {t('deactivateModifiers.confirmDeactivationButton')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
